import { useState } from "react";
import moment from "moment-timezone";
import { useIntl } from "react-intl";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Override from "../../../../models/ISupplier_Delivery_Schedule_Override";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import {
    IOverrideFormState,
    OverrideFormErrors,
    defaultOverrideDayFormState,
    defaultOverrideFormState,
    overrideFormStateToDTO,
    overrideToFormState,
    validateOverrideForm,
} from "./deliveryDeadlineUtils";
import "./DeliveryScheduleOverrideForm.scss";

type Props = {
    schedules: ISupplier_Delivery_Schedule[];
    existingOverride?: ISupplier_Delivery_Schedule_Override;
    onSaved: () => void;
    onCancel: () => void;
};

/**
 * Add/edit form for a schedule exception: one calendar week in which a template (or every
 * template) either delivers nothing or delivers on an explicit set of dates, each with its own
 * order-by date and time. Saving creates or fully replaces the exception.
 */
export const DeliveryScheduleOverrideForm = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [form, setForm] = useState<IOverrideFormState>(() =>
        props.existingOverride ? overrideToFormState(props.existingOverride) : defaultOverrideFormState()
    );
    const [errors, setErrors] = useState<OverrideFormErrors>({});
    const [isSaving, setIsSaving] = useState(false);

    const week = moment(form.week_start, "YYYY-MM-DD", true);
    const weekStart = week.isValid() ? week.clone().subtract(week.day(), "days") : null;
    const weekLabel = weekStart ? `${weekStart.format("ddd, MMM D")} – ${weekStart.clone().add(6, "days").format("ddd, MMM D, YYYY")}` : "";

    const setField = (field: keyof IOverrideFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const toggleNoDelivery = (e: any) => {
        setForm((prev) => ({ ...prev, no_delivery: !!e.target.value }));
    };

    const setDayField =
        (index: number, field: "delivery_date" | "hard_deadline_date" | "hard_deadline_time") =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setForm((prev) => ({ ...prev, days: prev.days.map((day, i) => (i === index ? { ...day, [field]: e.target.value } : day)) }));
        };

    const addDay = () => {
        setForm((prev) => ({ ...prev, days: [...prev.days, defaultOverrideDayFormState(weekStart || moment().startOf("day"))] }));
    };

    const removeDay = (index: number) => {
        setForm((prev) => ({ ...prev, days: prev.days.filter((_, i) => i !== index) }));
    };

    const save = async () => {
        const validationErrors = validateOverrideForm(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setIsSaving(true);
        try {
            const dto = overrideFormStateToDTO(form);
            if (props.existingOverride) {
                await DeliveryDeadlinesService.updateDeliveryScheduleOverride(props.existingOverride.guid, dto, userModel, supplierModel);
            } else {
                await DeliveryDeadlinesService.createDeliveryScheduleOverride(dto, userModel, supplierModel);
            }
            uiContext.showNotification({ message: "schedule_exception_saved", level: NotificationLevelEnum.success });
            props.onSaved();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="DeliveryScheduleOverrideForm rounded p-3 mt-2">
            <div className="row g-3">
                <div className="col-md-4">
                    <OwLabel title="applies_to" noBottomMargin={true} />
                    <select
                        aria-label="applies to template"
                        className="form-select form-select-sm"
                        value={form.schedule_guid}
                        onChange={setField("schedule_guid")}
                    >
                        <option value="">{intl.formatMessage({ id: "all_templates" })}</option>
                        {props.schedules.map((schedule) => (
                            <option key={schedule.guid} value={schedule.guid}>
                                {schedule.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4">
                    <OwLabel title="week_of" noBottomMargin={true} />
                    <input
                        type="date"
                        aria-label="week of"
                        className="form-control form-control-sm"
                        value={form.week_start}
                        onChange={setField("week_start")}
                    />
                    {weekLabel && <div className="text-muted small">{weekLabel}</div>}
                    {errors.week_start && <OwLabel title={errors.week_start} className="text-danger small" />}
                </div>
                <div className="col-md-4">
                    <OwLabel title="note" noBottomMargin={true} />
                    <input
                        type="text"
                        aria-label="exception note"
                        className="form-control form-control-sm"
                        value={form.note}
                        maxLength={255}
                        onChange={setField("note")}
                    />
                </div>
            </div>

            <div className="mt-3">
                <OwToggle
                    fn={toggleNoDelivery}
                    name="no_delivery"
                    label="no_deliveries_this_week"
                    active={form.no_delivery}
                    labelOnRight={true}
                />
            </div>

            {!form.no_delivery && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <OwLabel title="replacement_delivery_days" className="fw-bold" noBottomMargin={true} />
                        <OwButton
                            title="add_delivery_date"
                            leftIconClass="fas fa-plus"
                            fn={addDay}
                            className="btn-outline-primary btn-sm"
                        />
                    </div>
                    {errors.days && <OwLabel title={errors.days} className="text-danger small d-block mt-1" />}
                    <div className="table-responsive mt-2">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>
                                        <OwLabel title="delivery_date" noBottomMargin={true} />
                                    </th>
                                    <th>
                                        <OwLabel title="order_by_date" noBottomMargin={true} />
                                    </th>
                                    <th>
                                        <OwLabel title="order_by_time" noBottomMargin={true} />
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {form.days.map((day, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="date"
                                                aria-label={`delivery date ${index + 1}`}
                                                className="form-control form-control-sm"
                                                value={day.delivery_date}
                                                onChange={setDayField(index, "delivery_date")}
                                            />
                                            {errors.rows?.[index] && <OwLabel title={errors.rows[index]} className="text-danger small" />}
                                        </td>
                                        <td>
                                            <input
                                                type="date"
                                                aria-label={`order by date ${index + 1}`}
                                                className="form-control form-control-sm"
                                                value={day.hard_deadline_date}
                                                onChange={setDayField(index, "hard_deadline_date")}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="time"
                                                aria-label={`order by time ${index + 1}`}
                                                className="form-control form-control-sm"
                                                value={day.hard_deadline_time}
                                                onChange={setDayField(index, "hard_deadline_time")}
                                            />
                                        </td>
                                        <td className="text-end">
                                            <OwButton
                                                centerIconClass="fas fa-trash"
                                                fn={() => removeDay(index)}
                                                className="btn-outline-danger btn-sm"
                                                ariaLabel={`remove delivery date ${index + 1}`}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
                <OwButton title="cancel" fn={props.onCancel} className="btn-secondary btn-sm" disabled={isSaving} />
                <OwButton title="save" fn={save} className="btn-primary btn-sm" disabled={isSaving} />
            </div>
        </div>
    );
};

export default DeliveryScheduleOverrideForm;
